import { ResourceFormat } from "lib/filesUtils/ResourceFormat";
import { RefObject } from "react";

export interface FileInformation {
  /**
   * Файл.
   */
  file: Blob;

  /**
   * Имя файла.
   */
  filename: string;
}

/**
 * Набор методов для файлов.
 */
export class FilesUtils {
  /**
   * Открытие браузерной утилины для печати файла.
   *
   * @param file файл
   * @param filename имя файла
   */
  public static printFile = (file: Blob, filename: string) => {
    // workaround for IE
    if (window.navigator && (window as any).navigator.msSaveOrOpenBlob) {
      (window as any).navigator.msSaveOrOpenBlob(file, filename);
    } else {
      // Загружаем документ в iframe для того, чтобы распечатать в дальнейшем
      const iframe =  document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = URL.createObjectURL(file);
      
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }, 1000);
      };
      
      document.body.appendChild(iframe);
    }
  }
  
  /**
   * Скачивание файла.
   *
   * @param file файл
   * @param filename имя файла
   * @param downloadLink референс на скрытый элемент ссылки, который служит для активации загрузки файла
   */
  public static downloadFile = (file: Blob, filename: string, downloadLink: RefObject<any>) => {
    FilesUtils.createFileAsLink(file, filename, downloadLink, () => downloadLink.current.download = filename);
  }
  
  /**
   * Открытие файла в новой вкладке.
   *
   * @param file файл
   * @param filename имя файла
   * @param downloadLink референс на скрытый элемент ссылки, который служит для активации загрузки файла
   */
  public static openFileInNewTab = (file: Blob, filename: string, downloadLink: RefObject<any>) => {
    FilesUtils.createFileAsLink(file, filename, downloadLink, () => downloadLink.current.target = '_blank');
  }
  
  /**
   * Возвращает файл и наименование скачанного файла.
   *
   * @param response ответ от сервера
   * @param format формат файла отчёта
   */
  public static getFileInformation = (response: { data: any, headers: any }, format: ResourceFormat): FileInformation => {
    const contentDisposition = response.headers['content-disposition'];
    const contentType = response.headers['content-type'];
    let fileName;
    
    // необходимо для поддержки наименований файлов на английском и русском языках
    const encodings = ["filename=", "filename*=UTF-8''"];
    const supportedEncoding = encodings.find(encoding => contentDisposition?.includes(encoding));
    const rawFileName = supportedEncoding && contentDisposition?.split(supportedEncoding)[1];
    
    const formats = [`.${format.toLowerCase()}`, `.${format}`];
    
    if(!contentDisposition || !rawFileName) {
      fileName = 'report' + formats[0];
    } else {
      fileName = formats.some(format => rawFileName.includes(format))
          ? decodeURI(rawFileName)
          : decodeURI(rawFileName + formats[0]);
    }
    
    return {
      file: new Blob([response.data], {type: contentType}),
      filename: fileName
    };
  }
  
  /**
   * Создание файла в качестве ссылки.
   *
   * @param file файл
   * @param filename имя файла
   * @param downloadLink референс на скрытый элемент ссылки, который служит для активации загрузки файла
   * @param action действие для файла (например, открытие в новой вкладке или скачивание)
   */
  private static createFileAsLink = (file: Blob, filename: string, downloadLink: RefObject<any>, action: () => string) => {
    // workaround for IE
    if (window.navigator && (window as any).navigator.msSaveOrOpenBlob) {
      (window as any).navigator.msSaveOrOpenBlob(file, filename);
    } else {
      downloadLink.current.href = URL.createObjectURL(file);
      action();
      downloadLink.current.click();
    }
  }
}
